import * as api from "@/api";
import * as Time from "@/utils/time";

import Api from "@/api";

export type Person = api.Person;

export { useGetMe, getPerson, getPeople, updateProfile, useGetPeople } from "@/api";

export type SearchScope =
  | { type: "company"; id?: undefined }
  | { type: "project"; id: string }
  | { type: "space"; id: string }
  | { type: "goal"; id: string }
  | { type: "resource_hub"; id: string }
  | { type: "none"; id?: undefined };

export const CompanyWideSearchScope = { type: "company" } as SearchScope;
export const NoneSearchScope = { type: "none" } as SearchScope;

export const spaceScope = (spaceId: string) => {
  return { type: "space", id: spaceId } as SearchScope;
};

export function usePeopleSearch(scope: SearchScope) {
  //
  // There are multiple components that use this hook. Some of them
  // pass in a string, others pass in an object with a query property.
  // These components are not maintained in this repo, so we can't
  // change them easily to all use the same format.
  //
  // This is a bit of a hack to make it work with both.
  //
  return async (arg: string | { query: string; ignoredIds?: string[] }): Promise<Person[]> => {
    if (scope.type === "none") return [];

    let query = "";
    let ignoredIds: string[] = [];

    if (typeof arg === "string") {
      query = arg;
      ignoredIds = [];
    } else {
      query = arg.query;
      ignoredIds = arg.ignoredIds || [];
    }

    const res = await Api.searchPeople({
      query,
      ignoredIds,
      searchScopeType: scope.type,
      searchScopeId: scope.id,
    });
    return res.people as Person[];
  };
}

export function firstName(person: Pick<Person, "fullName">): string {
  return person.fullName!.split(" ")[0]!;
}

export function shortName(person: Pick<Person, "fullName">): string {
  return firstName(person) + " " + lastNameInitial(person) + ".";
}

function lastNameInitial(person: Pick<Person, "fullName">): string {
  return person.fullName!.split(" ").slice(-1)[0]![0]!;
}

export type NameFormat = "first" | "short" | "full";

export function formattedName(person: Pick<Person, "fullName">, nameFormat: NameFormat): string {
  if (nameFormat === "first") {
    return firstName(person);
  }

  if (nameFormat === "short") {
    return shortName(person);
  }

  return person.fullName!;
}

export function namesListToString(members: Person[]) {
  const names = members.map((person) => shortName(person));

  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  } else if (names.length > 2) {
    const last = names.pop();
    return `${names.join(", ")} and ${last}`;
  } else {
    return names[0]!;
  }
}

export function sortByName(people: Person[]): Person[] {
  return people
    .slice()
    .map((p) => p!)
    .sort((a, b) => a!.fullName!.localeCompare(b!.fullName!));
}

export function hasValidInvite(person: Person): boolean {
  if (!person.hasOpenInvitation) return false;
  if (!person.invitation?.expiresAt) return false;

  const time = Time.parse(person.invitation!.expiresAt!);
  if (!time) return false;

  return time > Time.now();
}

export function hasInvitationExpired(person: Person): boolean {
  if (!person.hasOpenInvitation) return false;
  if (!person.invitation?.expiresAt) return false;

  const time = Time.parse(person.invitation!.expiresAt!);
  if (!time) return false;

  return time < Time.now();
}
